import type { IDeviceRepository, PaginatedResponse, QueryParams } from '@yawi/domain';
import { buildQueryString, DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '@yawi/domain';
import type { AccessDevice, DeviceStatus, DeviceSyncResult, DeviceType } from '@yawi/types';
import { getApiClient } from '../../http/apiClient';

// Tipos de respuesta de la API
interface ApiDeviceResponse {
  id: string;
  name: string;
  type: 'VEHICLE_BARRIER' | 'PEDESTRIAN_GATE' | 'CAMERA_LPR' | 'CONTROLLER';
  ipAddress: string;
  location: string;
  status?: 'ONLINE' | 'OFFLINE';
  externalDeviceId?: string | null;
}

interface ApiCreateDeviceRequest {
  name: string;
  type: 'VEHICLE_BARRIER' | 'PEDESTRIAN_GATE' | 'CAMERA_LPR' | 'CONTROLLER';
  ipAddress: string;
  location: string;
}

interface ApiUpdateDeviceRequest {
  name?: string;
  type?: 'VEHICLE_BARRIER' | 'PEDESTRIAN_GATE' | 'CAMERA_LPR' | 'CONTROLLER';
  ipAddress?: string;
  location?: string;
  status?: 'ONLINE' | 'OFFLINE';
}

// Mapea la respuesta de la API al tipo AccessDevice interno
const mapApiDeviceToDevice = (apiDevice: ApiDeviceResponse): AccessDevice => ({
  id: apiDevice.id,
  name: apiDevice.name,
  type: apiDevice.type as DeviceType,
  ipAddress: apiDevice.ipAddress,
  location: apiDevice.location,
  status: (apiDevice.status || 'OFFLINE') as DeviceStatus,
  externalDeviceId: apiDevice.externalDeviceId ?? null,
});

export class ApiDeviceRepository implements IDeviceRepository {
  async getAllDevices(): Promise<AccessDevice[]> {
    const client = getApiClient();
    const response = await client.get<PaginatedResponse<ApiDeviceResponse>>('v1/access-control/devices');
    return response.data.map(mapApiDeviceToDevice);
  }

  async getDevices(params?: QueryParams): Promise<PaginatedResponse<AccessDevice>> {
    const client = getApiClient();
    const queryString = buildQueryString({
      page: params?.page ?? DEFAULT_PAGE,
      limit: params?.limit ?? DEFAULT_PAGE_SIZE,
      search: params?.search,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder,
      ...params,
    });
    const response = await client.get<PaginatedResponse<ApiDeviceResponse>>(
      `v1/access-control/devices?${queryString}`
    );
    return {
      ...response,
      data: response.data.map(mapApiDeviceToDevice),
    };
  }

  async createDevice(device: Omit<AccessDevice, 'id'>): Promise<AccessDevice> {
    const client = getApiClient();
    const request: ApiCreateDeviceRequest = {
      name: device.name,
      type: device.type,
      ipAddress: device.ipAddress,
      location: device.location,
    };
    const apiDevice = await client.post<ApiDeviceResponse>('v1/access-control/devices', request);
    return mapApiDeviceToDevice(apiDevice);
  }

  async updateDevice(device: AccessDevice): Promise<AccessDevice> {
    const client = getApiClient();
    const request: ApiUpdateDeviceRequest = {
      name: device.name,
      type: device.type,
      ipAddress: device.ipAddress,
      location: device.location,
      status: device.status,
    };
    const apiDevice = await client.put<ApiDeviceResponse>(`v1/access-control/devices/${device.id}`, request);
    return mapApiDeviceToDevice(apiDevice);
  }

  async deleteDevice(id: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`v1/access-control/devices/${id}`);
  }

  async getDeviceById(id: string): Promise<AccessDevice | null> {
    const client = getApiClient();
    try {
      const apiDevice = await client.get<ApiDeviceResponse>(`v1/access-control/devices/${id}`);
      return mapApiDeviceToDevice(apiDevice);
    } catch {
      return null;
    }
  }

  async deviceHeartbeat(id: string): Promise<void> {
    const client = getApiClient();
    await client.post(`v1/access-control/devices/${id}/heartbeat`);
  }

  async syncDevices(): Promise<DeviceSyncResult> {
    const client = getApiClient();
    return await client.post<DeviceSyncResult>('v1/access-control/devices/sync');
  }

  // Métodos de HikCentral
  async getHikCentralStatus(): Promise<{ configured: boolean; provider: string }> {
    const client = getApiClient();
    return await client.get('v1/devices/hikcentral/status');
  }

  async testHikCentralConnection(host: string, port: number): Promise<{ success: boolean }> {
    const client = getApiClient();
    return await client.post('v1/devices/hikcentral/test-connection', { host, port });
  }

  async listHikCentralDevices(): Promise<Array<{
    id: string;
    name: string;
    model: string;
    serialNumber: string;
    ipAddress: string;
    macAddress?: string;
    firmwareVersion?: string;
    status: 'online' | 'offline' | 'unknown';
    lastHeartbeat?: string;
  }>> {
    const client = getApiClient();
    return await client.get('v1/devices/hikcentral/devices');
  }

  async getHikCentralDevice(id: string): Promise<{
    id: string;
    name: string;
    model: string;
    serialNumber: string;
    ipAddress: string;
    macAddress?: string;
    firmwareVersion?: string;
    status: 'online' | 'offline' | 'unknown';
    lastHeartbeat?: string;
  }> {
    const client = getApiClient();
    return await client.get(`v1/devices/hikcentral/devices/${id}`);
  }

  async openDoor(deviceId: string, doorId: string, duration?: number): Promise<{
    success: boolean;
    message?: string;
    timestamp: string;
  }> {
    const client = getApiClient();
    return await client.post(`v1/devices/hikcentral/devices/${deviceId}/open`, {
      doorId,
      duration,
    });
  }

  async closeDoor(deviceId: string, doorId: string): Promise<{
    success: boolean;
    message?: string;
    timestamp: string;
  }> {
    const client = getApiClient();
    return await client.post(`v1/devices/hikcentral/devices/${deviceId}/close`, {
      doorId,
    });
  }

  async sendCommand(deviceId: string, command: {
    type: 'open_door' | 'close_door' | 'lock' | 'unlock' | 'reboot';
    doorId?: string;
    duration?: number;
  }): Promise<{
    success: boolean;
    message?: string;
    timestamp: string;
  }> {
    const client = getApiClient();
    return await client.post(`v1/devices/hikcentral/devices/${deviceId}/command`, command);
  }

  async getDeviceEvents(deviceId: string, limit?: number): Promise<Array<{
    eventId: string;
    deviceId: string;
    timestamp: string;
    eventType: 'access_granted' | 'access_denied' | 'door_opened' | 'door_closed' | 'alarm' | 'tamper';
    personId?: string;
    cardNumber?: string;
    doorId?: string;
    method?: string;
    details?: string;
  }>> {
    const client = getApiClient();
    const params = limit ? { limit } : undefined;
    return await client.get(`v1/devices/hikcentral/devices/${deviceId}/events`, { params });
  }

  async addPerson(deviceId: string, person: {
    personId: string;
    name: string;
    cardNumber?: string;
    faceData?: string;
    validFrom?: string;
    validTo?: string;
  }): Promise<{ success: boolean }> {
    const client = getApiClient();
    return await client.post(`v1/devices/hikcentral/devices/${deviceId}/persons`, person);
  }

  async removePerson(deviceId: string, personId: string): Promise<{ success: boolean }> {
    const client = getApiClient();
    return await client.post(`v1/devices/hikcentral/devices/${deviceId}/persons/${personId}/remove`);
  }

  async syncPersons(deviceId: string, persons: Array<{
    personId: string;
    name: string;
    cardNumber?: string;
    faceData?: string;
    validFrom?: string;
    validTo?: string;
  }>): Promise<{
    added: number;
    updated: number;
    removed: number;
  }> {
    const client = getApiClient();
    return await client.post(`v1/devices/hikcentral/devices/${deviceId}/sync-persons`, { persons });
  }
}

